import React, { useEffect, useMemo, useState } from 'react';
import api from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { toast } from 'sonner';
import { FileText, RefreshCcw, Printer, TrendingUp, AlertTriangle, DollarSign, Wallet } from 'lucide-react';

const REPORT_TYPES = [
  { value: 'monthly_summary', label: 'Monthly Summary' },
  { value: 'annual_summary', label: 'Annual Summary' },
  { value: 'income_report', label: 'Income Report' },
  { value: 'expense_report', label: 'Expense Report' },
  { value: 'category_report', label: 'Category Report' },
  { value: 'budget_performance', label: 'Budget Performance' },
  { value: 'loan_report', label: 'Loan Report' },
  { value: 'credit_report', label: 'Credit Report' },
  { value: 'cash_flow_report', label: 'Cash Flow Report' },
  { value: 'allocation_report', label: 'Allocation Report' },
];

function formatNumber(value) {
  return Number(value || 0).toLocaleString();
}

function formatMoney(value) {
  return `UGX ${formatNumber(value)}`;
}

function metricCard({ label, value, icon: Icon, note }) {
  return (
    <Card className="bg-white border border-slate-200 shadow-sm rounded-2xl">
      <CardContent className="p-4">
        <div className="flex items-start justify-between gap-3">
          <div>
            <p className="text-xs uppercase tracking-wide text-slate-500 font-medium">{label}</p>
            <div className="mt-2 text-2xl font-semibold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>{value}</div>
            {note ? <p className="mt-1 text-xs text-slate-500">{note}</p> : null}
          </div>
          <div className="w-10 h-10 rounded-2xl bg-emerald-50 flex items-center justify-center text-emerald-700">
            <Icon size={18} />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export default function BudgetReports() {
  const now = new Date();
  const [reportType, setReportType] = useState('monthly_summary');
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [year, setYear] = useState(now.getFullYear());
  const [search, setSearch] = useState('');
  const [report, setReport] = useState(null);
  const [summary, setSummary] = useState({});
  const [users, setUsers] = useState([]);
  const [filters, setFilters] = useState({});
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const load = async ({ nextReportType = reportType, nextMonth = month, nextYear = year, nextSearch = search } = {}) => {
    setRefreshing(true);
    try {
      const params = new URLSearchParams();
      params.set('type', nextReportType);
      params.set('month', String(nextMonth));
      params.set('year', String(nextYear));
      if (nextSearch.trim()) params.set('search', nextSearch.trim());
      const r = await api.get(`/admin/budget/reports?${params.toString()}`);
      setReport(r.data?.report || null);
      setSummary(r.data?.summary || {});
      setUsers(r.data?.users || []);
      setFilters(r.data?.filters || {});
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load budget reports');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => { load(); }, []);

  const cards = useMemo(() => report?.summary_cards || [], [report]);
  const chart = report?.charts?.monthly_comparison || [];
  const maxChartValue = Math.max(...chart.map((item) => Math.max(Number(item.income || 0), Number(item.expenses || 0))), 1);

  const onSearch = (event) => {
    event.preventDefault();
    load({ nextReportType: reportType, nextMonth: month, nextYear: year, nextSearch: search });
  };

  const handlePrint = () => window.print();

  if (loading) {
    return (
      <div className="flex items-center justify-center h-40">
        <div className="w-6 h-6 border-2 border-emerald-700 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
        <div>
          <h1 className="text-2xl md:text-3xl font-semibold text-slate-900 tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>Budget Reports</h1>
          <p className="text-slate-500 text-sm mt-0.5">Monthly and annual financial reports across Budget-enabled users.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={handlePrint}>
            <Printer size={14} className="mr-1.5" /> Print
          </Button>
          <Button className="bg-emerald-700 hover:bg-emerald-800 text-white" onClick={() => load()} disabled={refreshing}>
            <RefreshCcw size={14} className={`mr-1.5 ${refreshing ? 'animate-spin' : ''}`} /> Refresh
          </Button>
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        {metricCard({ label: 'Budget Users', value: summary.budget_enabled_users ?? 0, icon: Wallet, note: 'Users included in the current report set.' })}
        {metricCard({ label: 'Total Income', value: formatMoney(summary.total_income), icon: DollarSign, note: 'Combined income across filtered users.' })}
        {metricCard({ label: 'Net Change', value: formatMoney(summary.net_change), icon: TrendingUp, note: 'Income minus expenses for the selected period.' })}
        {metricCard({ label: 'Overspending Users', value: summary.overspending_users ?? 0, icon: AlertTriangle, note: 'Users with warnings or exceeded budgets.' })}
      </div>

      <Card className="bg-white border border-slate-200 shadow-sm rounded-2xl">
        <CardHeader className="space-y-4">
          <CardTitle className="text-lg" style={{ fontFamily: 'Outfit, sans-serif' }}>{report?.label || 'Report View'}</CardTitle>
          <form onSubmit={onSearch} className="grid gap-3 lg:grid-cols-[1fr_160px_160px_auto] lg:items-center">
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search by name, email or phone" />
            <Input type="number" min="1" max="12" value={month} onChange={(e) => setMonth(Number(e.target.value) || month)} />
            <Input type="number" min="2020" value={year} onChange={(e) => setYear(Number(e.target.value) || year)} />
            <Button type="submit" className="bg-slate-900 hover:bg-slate-800 text-white">Apply Filters</Button>
          </form>
          <div className="flex flex-wrap gap-2">
            {REPORT_TYPES.map((typeOption) => (
              <Button
                key={typeOption.value}
                type="button"
                variant={reportType === typeOption.value ? 'default' : 'outline'}
                className={reportType === typeOption.value ? 'bg-emerald-700 hover:bg-emerald-800 text-white' : ''}
                onClick={() => {
                  setReportType(typeOption.value);
                  load({ nextReportType: typeOption.value, nextMonth: month, nextYear: year, nextSearch: search });
                }}
              >
                {typeOption.label}
              </Button>
            ))}
          </div>
          <div className="flex flex-wrap gap-2 text-xs text-slate-500">
            <Badge className="bg-slate-100 text-slate-700">Month: {filters.month || month}</Badge>
            <Badge className="bg-slate-100 text-slate-700">Year: {filters.year || year}</Badge>
            <Badge className="bg-slate-100 text-slate-700">Reports: {summary.reports_generated ?? 0}</Badge>
            <Badge className="bg-slate-100 text-slate-700">Average Health: {summary.average_health_score ?? 0}</Badge>
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
            {cards.map((card) => (
              <Card key={card.label} className="border border-slate-200 bg-slate-50 rounded-2xl shadow-none">
                <CardContent className="p-4">
                  <div className="text-xs uppercase tracking-wide text-slate-500">{card.label}</div>
                  <div className="mt-2 text-xl font-semibold text-slate-900">{typeof card.value === 'number' ? formatMoney(card.value) : card.value}</div>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="rounded-2xl border border-slate-200 bg-white p-4">
            <div className="flex items-center justify-between gap-3 mb-4">
              <div>
                <h3 className="font-semibold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>Monthly Comparison</h3>
                <p className="text-sm text-slate-500">Income and expenses for the selected Budget users.</p>
              </div>
              <Badge className="bg-emerald-50 text-emerald-700 border border-emerald-100">Auto aggregated</Badge>
            </div>
            <div className="space-y-3">
              {chart.length ? chart.map((item) => (
                <div key={item.month} className="grid grid-cols-[96px_1fr_1fr_88px] items-center gap-3 text-sm">
                  <div className="text-slate-600">{item.month}</div>
                  <div className="h-3 rounded-full bg-slate-100 overflow-hidden">
                    <div className="h-full rounded-full bg-emerald-500" style={{ width: `${Math.max(8, (Number(item.income || 0) / maxChartValue) * 100)}%` }} />
                  </div>
                  <div className="h-3 rounded-full bg-slate-100 overflow-hidden">
                    <div className="h-full rounded-full bg-rose-500" style={{ width: `${Math.max(8, (Number(item.expenses || 0) / maxChartValue) * 100)}%` }} />
                  </div>
                  <div className="text-right text-slate-500">{formatMoney(Number(item.income || 0) - Number(item.expenses || 0))}</div>
                </div>
              )) : (
                <p className="text-sm text-slate-500">No comparison data available for this period.</p>
              )}
            </div>
          </div>

          <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
            <h3 className="font-semibold text-slate-900 mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>Insights and Alerts</h3>
            <div className="grid gap-3 md:grid-cols-2">
              {(report?.insights || []).map((item) => (
                <div key={item} className="rounded-xl border border-slate-200 bg-white p-3 text-sm text-slate-600">{item}</div>
              ))}
              {(report?.warnings || []).map((item) => (
                <div key={`${item.title}-${item.message}`} className={`rounded-xl border p-3 text-sm ${item.type === 'warning' ? 'border-amber-200 bg-amber-50 text-amber-800' : 'border-rose-200 bg-rose-50 text-rose-800'}`}>
                  <div className="font-medium">{item.title}</div>
                  <div className="mt-1">{item.message}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>User</TableHead>
                  <TableHead>Package</TableHead>
                  <TableHead>Income</TableHead>
                  <TableHead>Expenses</TableHead>
                  <TableHead>Net Change</TableHead>
                  <TableHead>Health</TableHead>
                  <TableHead>Warnings</TableHead>
                  <TableHead>Reports</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {users.map((user) => (
                  <TableRow key={user.id}>
                    <TableCell>
                      <div className="font-medium text-slate-900">{user.name}</div>
                      <div className="text-xs text-slate-500">{user.email || user.phone || user.id}</div>
                    </TableCell>
                    <TableCell>{user.package_name || 'Free'}</TableCell>
                    <TableCell>{formatMoney(user.income)}</TableCell>
                    <TableCell>{formatMoney(user.expenses)}</TableCell>
                    <TableCell>{formatMoney(user.net_change)}</TableCell>
                    <TableCell>
                      <Badge className={Number(user.health_score || 0) >= 75 ? 'bg-emerald-100 text-emerald-800' : Number(user.health_score || 0) >= 50 ? 'bg-amber-100 text-amber-800' : 'bg-rose-100 text-rose-800'}>
                        {user.health_status || 'Good'} · {user.health_score || 0}
                      </Badge>
                    </TableCell>
                    <TableCell>{user.warnings || 0}</TableCell>
                    <TableCell>{user.reports_generated || 0}</TableCell>
                  </TableRow>
                ))}
                {!users.length && (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center text-slate-500 py-10">
                      No budget reports found.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
